// Shared agency-switch logic, extracted from DashboardHeader.jsx's handleAgencySwitch so it
// can also be used by AgencyAlertGate.jsx to silently switch agency before opening a
// task/thread that was deep-linked from an FCM notification.
import { getFromIndexedDB } from './IndexedDBUtils';
import { getActiveData, getConfigData } from './Storage';
import { getAuthData } from './AuthUtils';
import { fetchAgencyData, fetchUsers } from '../Store/Reducers';

// Rebuilds every Redux slice from what's already cached in localStorage/IndexedDB for the
// currently active agency (read from the Jarvis auth row) - no API call, no LastSyncDT reset.
export const reloadAgencyData = async (dispatch) => {
  try {
    const activeAgencyID = Number(getAuthData()?.AgencyID || 0);
    if (!activeAgencyID) return;

    const connsData = await getFromIndexedDB('Conns') || [];
    const filteredConns = connsData.filter(conn => Number(conn.AgencyID) === activeAgencyID);

    const actsData = await getFromIndexedDB('Threads') || [];
    const filteredActs = actsData.filter(act => Number(act.AgencyID) === activeAgencyID);

    const connsMap = new Map();
    filteredConns.forEach(conn => {
      const connId = conn.ConnId || conn.ConnID || conn.connectionId || conn.id;
      if (connId) {
        connsMap.set(connId, conn);
        connsMap.set(String(connId), conn);
      }
    });
    const enrichedThreads = filteredActs.map(act => {
      const actConnId = act.ConnId || act.ConnID || act.connectionId || act.connection_id;
      if (!actConnId) return act;
      const matchingConn = connsMap.get(actConnId) || connsMap.get(String(actConnId)) || connsMap.get(Number(actConnId));
      return matchingConn ? { ...act, connectionData: matchingConn } : act;
    });

    const contractsData = await getFromIndexedDB('Contracts') || [];
    const filteredContracts = contractsData.filter(contract =>
      Number(contract.BuyerAgencyID) === activeAgencyID || Number(contract.SellerAgencyID) === activeAgencyID
    );

    // Tasks are loaded without an agency filter - a task can be linked to a connection/thread
    // from any of the user's agencies (see useTaskByID.jsx).
    const tasksData = await getFromIndexedDB('Tasks') || [];

    dispatch({ type: 'FETCH_CONNECTIONS_SUCCESS', payload: filteredConns });
    dispatch({ type: 'FETCH_ACTION_DATA_SUCCESS', payload: enrichedThreads });
    dispatch({ type: 'FETCH_CONTRACTS_SUCCESS', payload: filteredContracts });
    dispatch({ type: 'FETCH_TASKS_SUCCESS', payload: tasksData });
  } catch (error) {
    console.error('reloadAgencyData failed:', error);
  }
};

// Switches the active agency in localStorage + Redux only - deliberately does NOT navigate,
// so callers can decide whether to go home (manual switcher) or stay put (alert gate).
// Returns true on success, false if the target agency's credentials aren't available locally.
export const switchAgency = async (agencyID, dispatch) => {
  try {
    const userAgenciesRaw = localStorage.getItem('MyAgencies');
    const userAgencies = userAgenciesRaw ? JSON.parse(userAgenciesRaw) : [];
    const targetProfile = userAgencies.find(a => Number(a.AgencyID) === Number(agencyID));
    if (!targetProfile) {
      console.error('switchAgency: target agency credentials not found locally.');
      return false;
    }

    const jarvisRaw = localStorage.getItem('Jarvis');
    if (!jarvisRaw) {
      console.error('switchAgency: active session credentials not found.');
      return false;
    }
    const jarvisArray = JSON.parse(jarvisRaw);

    jarvisArray[0] = {
      ...jarvisArray[0],
      AgencyID: targetProfile.AgencyID,
      UserID: targetProfile.UserID,
      AgencyName: targetProfile.AgencyName || targetProfile.CompanyName || jarvisArray[0].AgencyName,
      UserName: targetProfile.UserName || jarvisArray[0].UserName,
      IsWeb: jarvisArray[0].IsWeb
    };

    document.cookie = "tempJarvis=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "tempJarvis=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname + ";";

    // B/C/D/U/V/SCRMConfigs already hold one row per agency the user has ever logged into -
    // don't clear them, just re-point Jarvis at the target agency/user so every read now
    // resolves to that agency's row.
    localStorage.setItem('Jarvis', JSON.stringify(jarvisArray));

    await reloadAgencyData(dispatch);
    dispatch(fetchAgencyData());
    dispatch(fetchUsers());
    dispatch({ type: 'UPDATE_ACTIVE_DATA_SUCCESS', payload: getActiveData() });
    dispatch({ type: 'UPDATE_CONFIG_SUCCESS', payload: getConfigData() });

    return true;
  } catch (error) {
    console.error('switchAgency failed:', error);
    return false;
  }
};

// Looks up which agency a task/thread/connection ID belongs to, straight from IndexedDB
// (unfiltered by active agency), so an alert deep link can be resolved before the target
// page's own agency-scoped Redux data has switched over.
export const getAlertAgencyID = async ({ taskID, threadID, connID }) => {
  try {
    if (taskID) {
      const tasks = await getFromIndexedDB('Tasks') || [];
      const task = tasks.find(t => String(t.TaskID) === String(taskID));
      if (task?.AgencyID) return Number(task.AgencyID);
    }
    if (threadID) {
      const threads = await getFromIndexedDB('Threads') || [];
      const thread = threads.find(t => String(t.ThreadID) === String(threadID));
      if (thread?.AgencyID) return Number(thread.AgencyID);
    }
    if (connID) {
      const conns = await getFromIndexedDB('Conns') || [];
      const conn = conns.find(c => String(c.ConnID) === String(connID));
      if (conn?.AgencyID) return Number(conn.AgencyID);
    }
  } catch (error) {
    console.error('getAlertAgencyID failed:', error);
  }
  return null;
};

// One-shot orchestrator for AgencyAlertGate: resolves the alert's agency, and switches to it
// if it differs from the currently active one. Returns true if a switch happened.
export const switchAgencyOnAlert = async ({ taskID, threadID, connID }, dispatch) => {
  const alertAgencyID = await getAlertAgencyID({ taskID, threadID, connID });
  if (!alertAgencyID) return false;

  const activeAgencyID = Number(getAuthData()?.AgencyID || 0);
  if (alertAgencyID === activeAgencyID) return false;

  return switchAgency(alertAgencyID, dispatch);
};
